import {
  useCallback,
  useEffect,
  useId,
  useInsertionEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import {
  Accessibility,
  Apple,
  ChevronDown,
  FileInput,
  FileOutput,
  Gauge,
  Globe,
  Languages,
  LayoutGrid,
  Menu,
  Link2,
  MonitorDown,
  Pencil,
  Search,
  X,
} from 'lucide-react';
import { ensureDesignSystemTokens } from '@/lib/design-system';
import { BrandLogo } from '@/lib/brand/logo';
import { getBrand } from '@/lib/brand';
import { CATEGORY_LABELS, CATEGORY_ORDER, TOOLS, type ToolCategory } from '@/features/tools/data';
import { capture } from '@/lib/posthog';
import { cn } from '@/lib/utils';
import {
  APP_NAV_ITEMS,
  DESKTOP_CLI_DOCS_URL,
  PUBLIC_DOWNLOAD_CTA,
  type AppNavItem,
} from '@/lib/downloads';
import { MarketingAccountCta } from '@/components/marketing-account-cta';
import { openToolCommandPalette } from '@/features/tools';
import {
  changeLocale,
  currentLocale,
  isLocalizedPublicPath,
  useTranslation,
  type SupportedLocale,
} from '@/i18n';

import { Button, buttonVariants } from '@/components/ui';

export type MarketingTheme = 'light' | 'dark';
export type MarketingSurface = 'default' | 'public-tools' | 'desktop-cli';

const MARKETING_THEME_KEY = 'okra-marketing-theme';
export const DISCORD_INVITE_URL = 'https://discord.gg/cjrGhfPjj';
// The GitHub org moved okraocr → okracomputer (2026-09-23); the old URLs 404.
// The Mac app source is private — desktop-releases is its public repo
// (installers, release notes, issues).
const GITHUB_REPO_URL = 'https://github.com/okracomputer/desktop-releases';
const SELF_HOST_RUNTIME_URL = 'https://github.com/okracomputer/server';

function getInitialMarketingTheme(): MarketingTheme {
  if (typeof window === 'undefined') return 'light';

  const stored = window.localStorage.getItem(MARKETING_THEME_KEY);
  if (stored === 'light' || stored === 'dark') return stored;

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ensureMarketingAssets(): void {
  if (typeof document === 'undefined') return;

  ensureDesignSystemTokens();
}

export function useMarketingTheme(forceTheme?: MarketingTheme) {
  const [theme, setTheme] = useState<MarketingTheme>(
    () => forceTheme ?? getInitialMarketingTheme(),
  );

  useInsertionEffect(() => {
    ensureMarketingAssets();
  }, []);

  useEffect(() => {
    if (forceTheme) {
      setTheme(forceTheme);
      return;
    }
    setTheme(getInitialMarketingTheme());
  }, [forceTheme]);

  useEffect(() => {
    if (forceTheme) return;
    window.localStorage.setItem(MARKETING_THEME_KEY, theme);
  }, [theme, forceTheme]);

  // Mirror the active theme onto <html> so the global `[data-theme='dark']`
  // design tokens are applied (or cleared) at the root. Without this, an
  // inherited dark root scope — set by the pre-paint script in index.html —
  // overrides the light tokens and makes the toggle a one-way trip to dark.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const toggleTheme = useCallback(() => {
    if (forceTheme) return;
    setTheme((value) => (value === 'dark' ? 'light' : 'dark'));
  }, [forceTheme]);

  return { theme, toggleTheme };
}

export function MarketingShell({
  children,
  className,
  forceTheme,
  showThemeToggle = true,
  surface = 'default',
  headerCta = 'account',
}: {
  children: ReactNode;
  className?: string;
  forceTheme?: MarketingTheme;
  /**
   * The desktop pages (`/download`, `/models`) have focused product
   * navigation; its section links point into `/download`, where the desktop
   * sections live since W.4.107. Other surfaces retain the account navigation.
   */
  surface?: MarketingSurface;
  /**
   * Header action on the desktop surface (W.4.104). Sign Up by default; only
   * the download page shows the Download button.
   */
  headerCta?: 'account' | 'download';
  showThemeToggle?: boolean;
}) {
  const { theme, toggleTheme } = useMarketingTheme(forceTheme);

  return (
    <div
      className={cn(
        'okra-marketing-home min-h-screen bg-[var(--paper-sunk)] text-[var(--ink)]',
        className,
      )}
      data-theme={theme}
    >
      <MarketingSiteHeader
        theme={theme}
        onToggleTheme={toggleTheme}
        showThemeToggle={showThemeToggle}
        desktopOnly={surface === 'desktop-cli'}
        downloadCta={headerCta === 'download'}
      />
      {children}
      <MarketingSiteFooter desktopOnly={surface === 'desktop-cli'} />
    </div>
  );
}

interface FooterLink {
  label: string;
  href: string;
  /** Render as a plain <a> (cross-worker or off-site) instead of a SPA <Link>. */
  external?: boolean;
  /** Open in a new tab — for truly off-domain links (docs, Figma). */
  newTab?: boolean;
  badge?: string;
}

interface FooterColumn {
  heading: string;
  links: FooterLink[];
}

const FOOTER_COLUMNS: FooterColumn[] = [
  {
    heading: 'Products',
    links: [
      { label: 'Desktop app', href: '/download' },
      { label: 'Parse', href: '/parse' },
      { label: 'Extract', href: '/extract' },
      // The Platform dropdown used to be the only internal link to /ask and
      // /redact (W.4.55 replaced it with Benchmarks). Keep them reachable here
      // so neither page becomes an orphan.
      { label: 'MCP Apps', href: '/ask' },
      { label: 'Redact', href: '/redact' },
      { label: 'PDF agents', href: '/pdf-agents' },
      { label: 'PDF MCP', href: '/pdf-mcp' },
      { label: 'PDF CLI', href: '/pdf-cli-toolsets' },
      { label: 'Self-host', href: SELF_HOST_RUNTIME_URL, external: true, newTab: true },
      { label: 'Workflows', href: '/workflows' },
      { label: 'A11y Audit', href: '/audit' },
      { label: 'PDF to Figma', href: '/pdf-to-figma' },
      { label: 'Chrome extension', href: '/chrome-extension' },
    ],
  },
  {
    heading: 'Tools',
    links: [
      { label: 'Merge PDF', href: '/tools/merge' },
      { label: 'Split PDF', href: '/tools/split' },
      { label: 'Compress PDF', href: '/tools/compress' },
      { label: 'PDF to Word', href: '/tools/pdf-to-word' },
      { label: 'PDF to Excel', href: '/tools/pdf-to-excel' },
      { label: 'PDF to HTML', href: '/tools/pdf-to-html' },
      { label: 'PDF to Markdown', href: '/tools/pdf-to-markdown' },
      { label: 'All tools', href: '/tools' },
    ],
  },
  {
    heading: 'Resources',
    links: [
      { label: 'Dev Docs', href: 'https://docs.okrapdf.com', external: true, newTab: true },
      { label: 'Blog', href: '/blog', external: true },
      { label: 'Benchmarks', href: '/benchmarks' },
      { label: 'GitHub', href: GITHUB_REPO_URL, external: true, newTab: true },
      { label: 'Discord', href: DISCORD_INVITE_URL, external: true, newTab: true },
      { label: 'Industries', href: '/for' },
    ],
  },
];

const DESKTOP_FOOTER_COLUMNS: FooterColumn[] = [
  {
    heading: 'Desktop',
    links: [
      { label: 'Download for Mac', href: '/download#macos' },
      { label: 'Windows waitlist', href: '/download#windows' },
      { label: 'Release notes', href: `${GITHUB_REPO_URL}/releases`, external: true },
    ],
  },
  {
    heading: 'Tools',
    links: [{ label: 'All tools', href: '/tools' }],
  },
  {
    heading: 'Community',
    links: [
      { label: 'Report an issue', href: `${GITHUB_REPO_URL}/issues`, external: true },
      { label: 'Discord', href: DISCORD_INVITE_URL, external: true },
      { label: 'Local benchmarks', href: '/benchmarks/parsebench-local' },
    ],
  },
];

const FOOTER_HEADING_KEYS: Record<string, string> = {
  Products: 'shell.footer.products',
  Tools: 'shell.footer.tools',
  Resources: 'shell.footer.resources',
};

const FOOTER_LABEL_KEYS: Record<string, string> = {
  Parse: 'shell.footer.parse',
  Extract: 'shell.footer.extract',
  'PDF agents': 'shell.footer.pdfAgents',
  'PDF MCP': 'shell.footer.pdfMcp',
  'PDF CLI': 'shell.footer.pdfCli',
  'Self-host': 'shell.footer.selfHost',
  Workflows: 'shell.footer.workflows',
  'A11y Audit': 'shell.footer.audit',
  'PDF to Figma': 'shell.footer.pdfToFigma',
  'Chrome extension': 'shell.footer.chromeExtension',
  'Merge PDF': 'shell.footer.mergePdf',
  'Split PDF': 'shell.footer.splitPdf',
  'Compress PDF': 'shell.footer.compressPdf',
  'PDF to Word': 'shell.footer.pdfToWord',
  'PDF to Excel': 'shell.footer.pdfToExcel',
  'PDF to HTML': 'shell.footer.pdfToHtml',
  'PDF to Markdown': 'shell.footer.pdfToMarkdown',
  'All tools': 'shell.footer.allTools',
  'Dev Docs': 'shell.nav.devDocs',
  Blog: 'shell.nav.blog',
  Benchmarks: 'shell.footer.benchmark',
  GitHub: 'shell.footer.github',
  Discord: 'shell.footer.discord',
  Industries: 'shell.footer.industries',
};

function FooterLinkItem({ link }: { link: FooterLink }) {
  const { t } = useTranslation();
  const className =
    'inline-flex items-center gap-1.5 text-[13px] text-[var(--ink-mute)] transition hover:text-[var(--ink)]';
  const onClick = () => capture('marketing_footer_click', { label: link.label, href: link.href });
  const badge = link.badge ? (
    <span className="rounded-full bg-[var(--paper-sunk)] px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-wide text-[var(--ink-mute)]">
      {link.badge}
    </span>
  ) : null;

  if (link.external) {
    return (
      <a
        href={link.href}
        onClick={onClick}
        className={className}
        {...(link.newTab ? { target: '_blank', rel: 'noreferrer' } : {})}
      >
        {t(FOOTER_LABEL_KEYS[link.label] ?? link.label, { defaultValue: link.label })}
        {badge}
      </a>
    );
  }

  return (
    <Link to={link.href} onClick={onClick} className={className}>
      {t(FOOTER_LABEL_KEYS[link.label] ?? link.label, { defaultValue: link.label })}
      {badge}
    </Link>
  );
}

function MarketingSiteFooter({ desktopOnly = false }: { desktopOnly?: boolean }) {
  const { t } = useTranslation();
  const brand = getBrand();
  return (
    <footer className="border-t border-[var(--line)] bg-[var(--paper-sunk)]">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-[1.4fr_repeat(3,1fr)]">
          <div className="col-span-2 sm:col-span-3 lg:col-span-1">
            <a href="/" className="flex items-center" aria-label={t('shell.nav.home')}>
              <BrandLogo decorative size={32} className="h-8 w-8" />
            </a>
          </div>
          {(desktopOnly ? DESKTOP_FOOTER_COLUMNS : FOOTER_COLUMNS).map((column) => (
            <div key={column.heading}>
              <h3 className="text-[12px] font-semibold uppercase tracking-wide text-[var(--ink-soft)]">
                {t(FOOTER_HEADING_KEYS[column.heading] ?? column.heading, {
                  defaultValue: column.heading,
                })}
              </h3>
              <ul className="mt-3 flex flex-col gap-2.5">
                {column.links.map((link) => (
                  <li key={link.label}>
                    <FooterLinkItem link={link} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-12 flex flex-col items-start justify-between gap-3 border-t border-[var(--line)] pt-6 text-[12px] text-[var(--ink-mute)] sm:flex-row sm:items-center">
          <div>
            © {new Date().getFullYear()} {brand.name}
          </div>
          <nav className="flex items-center gap-4">
            <Link to="/privacy" className="transition hover:text-[var(--ink)]">
              {t('shell.footer.privacy')}
            </Link>
            <Link to="/terms" className="transition hover:text-[var(--ink)]">
              {t('shell.footer.terms')}
            </Link>
            <a
              href={desktopOnly ? DESKTOP_CLI_DOCS_URL : 'https://docs.okrapdf.com'}
              target="_blank"
              rel="noreferrer"
              className="transition hover:text-[var(--ink)]"
            >
              {desktopOnly ? 'CLI docs' : t('shell.nav.devDocs')}
            </a>
          </nav>
        </div>
      </div>
    </footer>
  );
}

const TOOL_CATEGORY_META: Record<ToolCategory, { icon: typeof Link2; body: string }> = {
  accessibility: { icon: Accessibility, body: 'PDF/UA, Matterhorn & tagged-PDF checks.' },
  organize: { icon: LayoutGrid, body: 'Merge, split, and reorder pages.' },
  edit: { icon: Pencil, body: 'Redact, sign, watermark, rotate.' },
  'convert-to': { icon: FileInput, body: 'Excel, Word, and images to PDF.' },
  'convert-from': { icon: FileOutput, body: 'PDF to Markdown, JSON, Excel, images.' },
  optimize: { icon: Gauge, body: 'Compress, OCR, flatten, compare.' },
};

function GitHubMark({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className={className} fill="currentColor">
      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-4 w-4">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10 5.25a4.75 4.75 0 1 1 0 9.5 4.75 4.75 0 0 1 0-9.5ZM10 2v1.5M10 16.5V18M3.6 3.6l1.05 1.05M15.35 15.35l1.05 1.05M2 10h1.5M16.5 10H18M3.6 16.4l1.05-1.05M15.35 4.65 16.4 3.6"
      />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-4 w-4">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.9 12.8A6.3 6.3 0 0 1 7.2 4.1a6.8 6.8 0 1 0 8.7 8.7Z"
      />
    </svg>
  );
}

export function DiscordMark({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 256 199" className={className} fill="currentColor">
      <path d="M216.856 16.597A208.5 208.5 0 0 0 164.042 0c-2.275 4.113-4.933 9.645-6.766 14.046q-29.538-4.442-58.533 0c-1.832-4.4-4.55-9.933-6.846-14.046a207.8 207.8 0 0 0-52.855 16.638C5.618 67.147-3.443 116.4 1.087 164.956c22.169 16.555 43.653 26.612 64.775 33.193A161 161 0 0 0 79.735 175.3a136.4 136.4 0 0 1-21.846-10.632 109 109 0 0 0 5.356-4.237c42.122 19.702 87.89 19.702 129.51 0a132 132 0 0 0 5.355 4.237 136 136 0 0 1-21.886 10.653c4.006 8.02 8.638 15.67 13.873 22.848 21.142-6.58 42.646-16.637 64.815-33.213 5.316-56.288-9.08-105.09-38.056-148.36M85.474 135.095c-12.645 0-23.015-11.805-23.015-26.18s10.149-26.2 23.015-26.2 23.236 11.804 23.015 26.2c.02 14.375-10.148 26.18-23.015 26.18m85.051 0c-12.645 0-23.014-11.805-23.014-26.18s10.148-26.2 23.014-26.2c12.867 0 23.236 11.804 23.015 26.2 0 14.375-10.148 26.18-23.015 26.18" />
    </svg>
  );
}

export function MarketingSiteHeader({
  theme,
  onToggleTheme,
  sticky = true,
  showThemeToggle = true,
  desktopOnly = false,
  downloadCta = false,
}: {
  theme: MarketingTheme;
  onToggleTheme: () => void;
  sticky?: boolean;
  showThemeToggle?: boolean;
  desktopOnly?: boolean;
  /** The download page's header keeps the Download button (W.4.104). */
  downloadCta?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <header
      className={cn(
        'z-40 shrink-0 border-b border-[var(--line)] bg-[color-mix(in_srgb,var(--paper-sunk)_88%,transparent)] backdrop-blur',
        sticky ? 'sticky top-0' : 'relative',
      )}
    >
      <div className="mx-auto flex min-h-16 max-w-7xl flex-wrap items-center justify-between gap-x-2 gap-y-2 px-4 py-2 sm:px-6 lg:px-8">
        <a href="/" className="flex items-center" aria-label={t('shell.nav.home')}>
          <BrandLogo size={32} className="h-8 w-8" />
        </a>

        {desktopOnly ? (
          <nav
            aria-label="Primary"
            className="hidden items-center gap-1 text-sm text-[var(--ink-soft)] md:flex"
          >
            <a href="/download#workflow" className="px-3 py-2 hover:text-[var(--ink)]">
              How it works
            </a>
            <a href="/download#benchmarks" className="px-3 py-2 hover:text-[var(--ink)]">
              Benchmarks
            </a>
            <a href="/models" className="px-3 py-2 hover:text-[var(--ink)]">
              Model picker
            </a>
            <a href="/download#local" className="px-3 py-2 hover:text-[var(--ink)]">
              Why local
            </a>
            <a href="/download" className="px-3 py-2 hover:text-[var(--ink)]">
              Desktop app
            </a>
            <a href={GITHUB_REPO_URL} className="hidden px-3 py-2 hover:text-[var(--ink)] lg:block">
              GitHub
            </a>
          </nav>
        ) : (
          <nav aria-label="Primary" className="hidden items-center gap-1 text-[13px] font-medium text-[var(--ink-soft)] xl:flex">
            <ToolsMenu />
            <AppsMenu />
            <Link
              className="px-3 py-2 transition hover:text-[var(--ink)]"
              to="/benchmarks"
              onClick={() =>
                capture('marketing_nav_click', { destination: 'benchmarks', href: '/benchmarks' })
              }
            >
              {t('shell.nav.benchmarks', { defaultValue: 'Benchmarks' })}
            </Link>
            <a
              className="px-3 py-2 transition hover:text-[var(--ink)]"
              href="/blog"
              onClick={() => capture('marketing_nav_click', { destination: 'blog', href: '/blog' })}
            >
              {t('shell.nav.blog')}
            </a>
            <a
              className="px-3 py-2 transition hover:text-[var(--ink)]"
              href="https://docs.okrapdf.com"
              onClick={() =>
                capture('marketing_nav_click', {
                  destination: 'docs',
                  href: 'https://docs.okrapdf.com',
                })
              }
            >
              {t('shell.nav.devDocs')}
            </a>
            <a
              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[var(--ink-soft)] transition hover:bg-[var(--paper-raised)] hover:text-[var(--ink)]"
              href={SELF_HOST_RUNTIME_URL}
              target="_blank"
              rel="noreferrer"
              aria-label={t('shell.nav.selfHost')}
              title={t('shell.footer.selfHost')}
              onClick={() =>
                capture('marketing_nav_click', {
                  destination: 'self_host',
                  href: SELF_HOST_RUNTIME_URL,
                })
              }
            >
              <GitHubMark className="h-4 w-4" />
            </a>
          </nav>
        )}

        <div className="flex items-center gap-2">
          <a
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-[var(--ink-soft)] transition hover:bg-[var(--paper-raised)] hover:text-[var(--ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            href={DISCORD_INVITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('home.hero.discordSupport')}
            title={t('home.hero.discordSupport')}
            onClick={() =>
              capture('marketing_nav_click', { destination: 'discord', href: DISCORD_INVITE_URL })
            }
          >
            <DiscordMark className="h-5 w-5" />
          </a>
          {!desktopOnly ? (
            <>
              <div className="hidden sm:block">
                <LanguageSwitcher />
              </div>
              <Button
                variant="unstyled"
                type="button"
                onClick={() => openToolCommandPalette('marketing_header')}
                aria-label={`${t('tools.index.searchLabel')} (⌘K)`}
                title={`${t('tools.index.searchLabel')} (⌘K)`}
                className="inline-flex h-9 items-center gap-2 rounded-md border border-[var(--line)] bg-[var(--button-secondary-bg)] px-2.5 text-[13px] font-medium text-[var(--ink-mute)] transition hover:border-[var(--selected-line)] hover:text-[var(--ink)]"
              >
                <Search className="h-3.5 w-3.5" />
                <span className="hidden lg:inline">{t('tools.index.searchLabel')}</span>
                <kbd className="hidden rounded border border-[var(--line)] px-1 py-0.5 text-[10px] lg:inline">
                  ⌘K
                </kbd>
              </Button>
            </>
          ) : null}
          {showThemeToggle ? (
            <Button
              variant="unstyled"
              type="button"
              onClick={onToggleTheme}
              aria-label={t('shell.theme.switchTo', {
                mode: t(theme === 'dark' ? 'shell.theme.light' : 'shell.theme.dark'),
              })}
              title={t('shell.theme.switchTo', {
                mode: t(theme === 'dark' ? 'shell.theme.light' : 'shell.theme.dark'),
              })}
              aria-pressed={theme === 'dark'}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[var(--line)] bg-[var(--button-secondary-bg)] text-[var(--ink-mute)] transition hover:border-[var(--selected-line)] hover:text-[var(--ink)]"
            >
              {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
              <span className="sr-only">
                {t(theme === 'dark' ? 'shell.theme.light' : 'shell.theme.dark')}
              </span>
            </Button>
          ) : null}
          {desktopOnly && downloadCta ? (
            // Only the download page shows it, so it points at the Mac card there
            // (W.4.107): a link to the page itself would do nothing.
            <Link
              to="/download#macos"
              className={buttonVariants({ size: 'sm' })}
              onClick={() =>
                capture('marketing_nav_click', {
                  destination: 'desktop_download',
                  href: '/download#macos',
                })
              }
            >
              Download
            </Link>
          ) : (
            <MarketingAccountCta
              variant={desktopOnly ? 'studio' : 'marketing'}
              source="marketing_header"
              pin={desktopOnly ? 'sign_up' : undefined}
            />
          )}
          <MobileSiteMenu desktopOnly={desktopOnly} />
        </div>
      </div>
    </header>
  );
}

function MobileSiteMenu({ desktopOnly }: { desktopOnly: boolean }) {
  const { t } = useTranslation();
  const { open, setOpen, ref } = useDropdown();
  const menuId = useId();
  const links = desktopOnly
    ? [
        { label: 'How it works', href: '/download#workflow' },
        { label: 'Benchmarks', href: '/download#benchmarks' },
        { label: 'Model picker', href: '/models' },
        { label: 'Why local', href: '/download#local' },
        { label: 'Desktop app', href: '/download' },
        { label: 'GitHub', href: GITHUB_REPO_URL },
      ]
    : [
        { label: t('shell.nav.tools'), href: '/tools' },
        { label: t('shell.nav.apps'), href: '/download' },
        { label: t('shell.nav.benchmarks', { defaultValue: 'Benchmarks' }), href: '/benchmarks' },
        { label: t('shell.nav.blog'), href: '/blog' },
        { label: t('shell.nav.devDocs'), href: 'https://docs.okrapdf.com' },
        { label: t('shell.nav.selfHost'), href: SELF_HOST_RUNTIME_URL },
        { label: 'Discord', href: DISCORD_INVITE_URL },
      ];

  return (
    <div
      ref={ref}
      className={desktopOnly ? 'md:hidden' : 'xl:hidden'}
      onKeyDown={(event) => {
        if (event.key === 'Escape') ref.current?.querySelector('button')?.focus();
      }}
    >
      <Button
        variant="outline"
        size="icon"
        aria-label={t(open ? 'shell.nav.closeNavigation' : 'shell.nav.openNavigation')}
        title={t(open ? 'shell.nav.closeNavigation' : 'shell.nav.openNavigation')}
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((value) => !value)}
      >
        {open ? <X aria-hidden /> : <Menu aria-hidden />}
      </Button>
      {open ? (
        <nav
          id={menuId}
          aria-label={t('shell.nav.mobileNavigation')}
          className="absolute inset-x-0 top-full max-h-[calc(100dvh-5rem)] overflow-y-auto border-b border-border bg-card px-4 py-3 shadow-menu sm:px-6"
        >
          <div className="mx-auto grid max-w-7xl gap-1 sm:grid-cols-2">
            {links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="flex min-h-11 items-center rounded-md px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                onClick={() => {
                  setOpen(false);
                  capture('marketing_nav_click', { destination: link.label, href: link.href });
                }}
              >
                {link.label}
              </a>
            ))}
          </div>
          {!desktopOnly ? (
            <div className="mt-3 border-t border-border px-3 pt-3 sm:hidden">
              <LanguageSwitcher />
            </div>
          ) : null}
        </nav>
      ) : null}
    </div>
  );
}

function LanguageSwitcher() {
  const { t } = useTranslation();
  const locale = currentLocale();

  if (typeof window !== 'undefined' && !isLocalizedPublicPath(window.location.pathname)) {
    return null;
  }

  return (
    <label className="relative inline-flex h-9 items-center rounded-md border border-[var(--line)] bg-[var(--button-secondary-bg)] text-[var(--ink-mute)] transition hover:border-[var(--selected-line)] hover:text-[var(--ink)]">
      <span className="sr-only">{t('shell.language.label')}</span>
      <Languages className="pointer-events-none absolute left-2 h-3.5 w-3.5" aria-hidden />
      {/* eslint-disable-next-line okra-ui/no-raw-primitives -- native language picker preserves OS select semantics */}
      <select
        value={locale}
        aria-label={t('shell.language.label')}
        onChange={(event) => {
          const next = event.target.value as SupportedLocale;
          capture('marketing_language_changed', { from: locale, to: next });
          void changeLocale(next);
        }}
        className="h-full appearance-none rounded-md bg-transparent py-0 pl-7 pr-2 text-[11px] font-semibold outline-none sm:text-[12px]"
      >
        <option value="en">{t('shell.language.english')}</option>
        <option value="zh-CN">简体中文</option>
      </select>
    </label>
  );
}

// Nav dropdown behavior for ToolsMenu / AppsMenu. The button's onClick is
// the source of truth (reliable toggle on mouse, touch, and keyboard via
// Enter/Space). Outside-click + Escape close the menu and keep aria-expanded in
// sync with `open`.
function useDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (e.target instanceof Node && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return { open, setOpen, ref } as const;
}

function ToolsMenu() {
  const { t } = useTranslation();
  const { open, setOpen, ref } = useDropdown();
  return (
    <div ref={ref} className="relative">
      <Button
        variant="unstyled"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        className="inline-flex items-center gap-1 px-3 py-2 transition hover:text-foreground"
      >
        {t('shell.nav.tools')}
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open ? 'rotate-180' : '')} />
      </Button>
      {open ? (
        <div className="absolute left-0 top-full z-50 w-[26rem] pt-2">
          <div className="overflow-hidden rounded-lg border border-[var(--line)] bg-[var(--paper-raised)] shadow-[0_24px_60px_-24px_var(--marketing-heavy-shadow)]">
            <div className="grid grid-cols-1 gap-px bg-[var(--line-soft)]">
              {CATEGORY_ORDER.map((category) => (
                <ToolsMenuItem key={category} category={category} />
              ))}
            </div>
            <Link
              to="/tools"
              onClick={() =>
                capture('marketing_nav_click', { destination: 'tools_all', href: '/tools' })
              }
              className="flex items-center justify-between border-t border-[var(--line)] bg-[var(--paper-raised)] px-4 py-3 text-[12px] font-semibold text-[var(--ink)] transition hover:bg-[var(--paper-sunk)]"
            >
              {t('shell.nav.browseAllTools', { count: TOOLS.length })}
              <ChevronDown className="h-3.5 w-3.5 -rotate-90" />
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const APP_NAV_ICONS: Record<AppNavItem['id'], typeof Apple> = {
  macos: Apple,
  windows: MonitorDown,
  chrome: Globe,
};

function AppsMenu() {
  const { t } = useTranslation();
  const { open, setOpen, ref } = useDropdown();
  return (
    <div ref={ref} className="relative">
      <Button
        variant="unstyled"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        className="inline-flex items-center gap-1 px-3 py-2 transition hover:text-foreground"
      >
        {t('shell.nav.apps', { defaultValue: 'Apps' })}
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open ? 'rotate-180' : '')} />
      </Button>
      {open ? (
        <div className="absolute left-0 top-full z-50 w-[22rem] pt-2">
          <div className="overflow-hidden rounded-lg border border-[var(--line)] bg-[var(--paper-raised)] shadow-[0_24px_60px_-24px_var(--marketing-heavy-shadow)]">
            <div className="grid grid-cols-1 gap-px bg-[var(--line-soft)]">
              {APP_NAV_ITEMS.map((item) => (
                <AppsMenuItem key={item.id} item={item} />
              ))}
            </div>
            <Link
              to={PUBLIC_DOWNLOAD_CTA.href}
              onClick={() =>
                capture('marketing_nav_click', {
                  destination: 'apps_all',
                  href: PUBLIC_DOWNLOAD_CTA.href,
                })
              }
              className="flex items-center justify-between border-t border-[var(--line)] bg-[var(--paper-raised)] px-4 py-3 text-[12px] font-semibold text-[var(--ink)] transition hover:bg-[var(--paper-sunk)]"
            >
              {t('shell.nav.browseDownloads', { defaultValue: 'All downloads' })}
              <ChevronDown className="h-3.5 w-3.5 -rotate-90" />
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AppsMenuItem({ item }: { item: AppNavItem }) {
  const { t } = useTranslation();
  const Icon = APP_NAV_ICONS[item.id];
  const title = t(`shell.apps.${item.id}.title`, { defaultValue: item.title });
  const body = t(`shell.apps.${item.id}.body`, { defaultValue: item.body });
  return (
    <Link
      to={item.href}
      onClick={() =>
        capture('marketing_nav_click', {
          destination: `apps_${item.id}`,
          href: item.href,
        })
      }
      className="group flex items-start gap-3 bg-[var(--paper-raised)] px-4 py-3 transition hover:bg-[var(--paper-sunk)]"
    >
      <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-[var(--line)] bg-[var(--paper-sunk)] text-[var(--ember-text)] transition group-hover:border-[var(--ember)]">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <span className="text-[13px] font-semibold text-[var(--ink)]">{title}</span>
        <p className="mt-0.5 text-[12px] leading-4 text-[var(--ink-mute)]">{body}</p>
      </div>
    </Link>
  );
}

function ToolsMenuItem({ category }: { category: ToolCategory }) {
  const { t } = useTranslation();
  const meta = TOOL_CATEGORY_META[category];
  const Icon = meta.icon;
  const count = TOOLS.filter((tool) => tool.category === category).length;
  return (
    <Link
      to={`/tools?category=${category}`}
      onClick={() =>
        capture('marketing_nav_click', {
          destination: `tools_${category}`,
          href: `/tools?category=${category}`,
        })
      }
      className="group flex items-start gap-3 bg-[var(--paper-raised)] px-4 py-3 transition hover:bg-[var(--paper-sunk)]"
    >
      <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-[var(--line)] bg-[var(--paper-sunk)] text-[var(--ember-text)] transition group-hover:border-[var(--ember)]">
        <Icon className="h-4 w-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-semibold text-[var(--ink)]">
            {t(
              `tools.categories.${category.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase())}`,
              { defaultValue: CATEGORY_LABELS[category] },
            )}
          </span>
          <span className="rounded-full bg-[var(--paper-sunk)] px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-wide text-[var(--ink-mute)]">
            {count}
          </span>
        </div>
        <p className="mt-0.5 text-[12px] leading-4 text-[var(--ink-mute)]">
          {t(
            `shell.categoryBody.${category.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase())}`,
            { defaultValue: meta.body },
          )}
        </p>
      </div>
    </Link>
  );
}
